import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useNavigate, ScrollRestoration } from 'react-router-dom';
import Loader from '../components/common/loader/Loader';
import useUserData from '../hooks/useUserData';
import {
  BOOKMARKS,
  HOME,
  DRAFTS,
  FEED,
  LOGIN,
  MY_PROFILE,
  EDIT_PROFILE,
  PEOPLE,
  PROJECT,
  PROJECTS,
  SEARCH,
  STAKEHOLDER,
  STAKEHOLDERS,
  USER_POSTS,
  USER_PROFILE,
  VIEW_ARTICLE,
  INSIGHTS,
  INSIGHT,
  NOTIFICATION,
  STAKEHOLDER_BULK_UPDATE,
  PEOPLE_BULK_UPDATE,
  PROJECT_BULK_UPDATE,
  VIEW_DRAFT,
  FILES,
  COLLECTIONS,
  STAKEHOLDER_FORM,
  CREATE_PROJECT_PROGRESS_CARD,
  CREATE_STAKEHOLDER_FORM,
  ADD_PROJECT,
  EDIT_PROJECT,
  PROJECT_SCORECARD,
  PROJECT_PROGRESS_CARD,
  CHAT,
  CREATE_PROJECT_SCORECARD,
  REQUESTS,
  ADD_PARTNER,
  EDIT_PARTNER,
  QUESTIONS,
  SUGGESTIONS,
  CREATE_RETROSPECTIVE_CARD,
  EDIT_RETROSPECTIVE_CARD,
  HELP_CENTER,
  TRACKERS,
  TRACKER_DETAILS,
} from '../constants/routes';
import MainWrapper from '../components/main/MainWrapper';
import { getAdminRoutePath } from '../utility/helpers';
import MainDrawer from 'components/drawer/MainDrawer';
import PageNotFound from './PageNotFound';
import BuildWiseLoader from './BuildWiseLoader';
import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';

const Home = lazy(() => import('@buildwise/pages/Home'));
const Feed = lazy(() => import('@buildwise/pages/Feed'));
const UserPosts = lazy(() => import('../pages/userPosts/UserPosts'));
const Bookmarks = lazy(() => import('../pages/bookmarks/Bookmarks'));
const Article = lazy(() => import('../pages/article/Article'));
const Chat = lazy(() => import('@buildwise/pages/Chat'));
const Projects = lazy(() => import('../pages/projects/Projects'));
const Project = lazy(() => import('../pages/project/Project'));
const UserForm = lazy(() => import('../pages/forms/UserForm'));
const ProjectForm = lazy(() => import('@buildwise/pages/ProjectForm'));
const People = lazy(() => import('@buildwise/pages/People'));
const BulkUpdateUsers = lazy(() => import('@buildwise/pages/BulkUpdateUsers'));
const Profile = lazy(() => import('@buildwise/pages/Profile'));
const EditProfile = lazy(() => import('@buildwise/pages/EditProfile'));
const MyQuestions = lazy(() => import('@buildwise/pages/MyQuestions'));
const MySuggestions = lazy(() => import('@buildwise/pages/MySuggestions'));
const Drafts = lazy(() => import('../pages/drafts/Drafts'));
const Requests = lazy(() => import('@buildwise/pages/Requests'));
const Stakeholders = lazy(() => import('../pages/stakeholders/Stakeholders'));
const AddPartner = lazy(() => import('@buildwise/pages/AddPartner'));
const StakeholdersBulkUpdate = lazy(() => import('../pages/stakeholders/BulkUpdateStakeholders'));
const Stakeholder = lazy(() => import('../pages/stakeholder/Stakeholder'));
const Search = lazy(() => import('../pages/search/Search'));
const Insights = lazy(() => import('@buildwise/pages/Insights'));
const CompanyFiles = lazy(() => import('pages/companyFiles/CompanyFiles'));
const Insight = lazy(() => import('../pages/insight/Insight'));
const Notifications = lazy(() => import('../pages/notifications/Notifications'));
const ProjectBulkUpdate = lazy(() => import('../pages/project/BulkUpdateProjects'));
const Playlists = lazy(() => import('@buildwise/pages/Playlists'));
const HelpCenter = lazy(() => import('@buildwise/pages/HelpCenter'));
const Trackers = lazy(() => import('@buildwise/pages/Trackers'));
const TrackerDetails = lazy(() => import('@buildwise/pages/TrackerDetails'));

const DashboardRoutes = () => {
  const navigate = useNavigate();
  const { user, userInfo, companyId, company, companyQueryInfo } = useUserData();
  /* 
    Instead of fetching this after GET /company & /user calls. Fetch this concurrently.
    This config is used in MainWrapper to determine if we need to prompt User Form to complete profile.
  */
  useConfigQuery({
    companyId: companyId,
    configName: CONFIGS.USER_INPUT_FIELDS.configName,
    shouldFetch: !!company,
  });

  useEffect(() => {
    if (!user && !userInfo.isLoading) {
      navigate(LOGIN);
    }
  }, [user, userInfo.isLoading]);

  if (userInfo.isLoading || companyQueryInfo.isLoading) {
    return <BuildWiseLoader />;
  }

  return (
    <MainWrapper sidebar={MainDrawer}>
      <Suspense fallback={<Loader variant="fixed" />}>
        <ScrollRestoration
          getKey={(location) => location.pathname}
          storageKey="router-scroll-positions"
        />

        <Routes>
          <Route path={getAdminRoutePath(HOME)} element={<Home />} />
          <Route path={getAdminRoutePath(FEED)} element={<Feed />} />
          <Route path={getAdminRoutePath(COLLECTIONS)} element={<Playlists />} />
          <Route path={getAdminRoutePath(SEARCH)} element={<Search />} />
          <Route path={getAdminRoutePath(CHAT)} element={<Chat />} />
          <Route path={getAdminRoutePath(VIEW_ARTICLE)} element={<Article />} />
          <Route path={getAdminRoutePath(VIEW_DRAFT)} element={<Article />} />
          <Route path={getAdminRoutePath(MY_PROFILE)} element={<Profile />} />
          <Route path={getAdminRoutePath(STAKEHOLDERS)} element={<Stakeholders />} />
          <Route path={getAdminRoutePath(ADD_PARTNER)} element={<AddPartner />} />
          <Route path={getAdminRoutePath(EDIT_PARTNER)} element={<AddPartner />} />
          <Route path={getAdminRoutePath(STAKEHOLDER)} element={<Stakeholder />} />
          <Route path={getAdminRoutePath(INSIGHTS)} element={<Insights />} />
          <Route path={getAdminRoutePath(TRACKERS)} element={<Trackers />} />
          <Route path={getAdminRoutePath(TRACKER_DETAILS)} element={<TrackerDetails />} />
          <Route path={getAdminRoutePath(FILES)} element={<CompanyFiles />} />
          <Route path={getAdminRoutePath(INSIGHT)} element={<Insight />} />
          <Route path={getAdminRoutePath(PROJECTS)} element={<Projects />} />
          <Route path={getAdminRoutePath(PROJECT)} element={<Project />} />
          <Route path={getAdminRoutePath(PROJECT_SCORECARD)} element={<UserForm />} />
          <Route path={getAdminRoutePath(PROJECT_PROGRESS_CARD)} element={<UserForm />} />
          <Route path={getAdminRoutePath(STAKEHOLDER_FORM)} element={<UserForm />} />
          <Route path={getAdminRoutePath(CREATE_PROJECT_PROGRESS_CARD)} element={<UserForm />} />
          <Route path={getAdminRoutePath(CREATE_PROJECT_SCORECARD)} element={<UserForm />} />
          <Route path={getAdminRoutePath(CREATE_STAKEHOLDER_FORM)} element={<UserForm />} />
          <Route path={getAdminRoutePath(CREATE_RETROSPECTIVE_CARD)} element={<UserForm />} />
          <Route path={getAdminRoutePath(EDIT_RETROSPECTIVE_CARD)} element={<UserForm />} />
          <Route path={getAdminRoutePath(ADD_PROJECT)} element={<ProjectForm />} />
          <Route path={getAdminRoutePath(EDIT_PROJECT)} element={<ProjectForm />} />
          <Route path={getAdminRoutePath(PEOPLE)} element={<People />} />
          <Route path={getAdminRoutePath(EDIT_PROFILE)} element={<EditProfile />} />
          <Route path={getAdminRoutePath(BOOKMARKS)} element={<Bookmarks />} />
          <Route path={getAdminRoutePath(QUESTIONS)} element={<MyQuestions />} />
          <Route path={getAdminRoutePath(SUGGESTIONS)} element={<MySuggestions />} />
          <Route path={getAdminRoutePath(DRAFTS)} element={<Drafts />} />
          <Route path={getAdminRoutePath(REQUESTS)} element={<Requests />} />
          <Route path={getAdminRoutePath(USER_POSTS)} element={<UserPosts />} />
          <Route path={getAdminRoutePath(USER_PROFILE)} element={<Profile />} />
          <Route path={getAdminRoutePath(NOTIFICATION)} element={<Notifications />} />
          <Route path={getAdminRoutePath(HELP_CENTER)} element={<HelpCenter />} />
          <Route
            path={getAdminRoutePath(STAKEHOLDER_BULK_UPDATE)}
            element={<StakeholdersBulkUpdate />}
          />
          <Route path={getAdminRoutePath(PEOPLE_BULK_UPDATE)} element={<BulkUpdateUsers />} />
          <Route path={getAdminRoutePath(PROJECT_BULK_UPDATE)} element={<ProjectBulkUpdate />} />
          <Route path="/*" element={<PageNotFound />} />
        </Routes>
      </Suspense>
    </MainWrapper>
  );
};

export default DashboardRoutes;
